import { Box, Button } from '@eatclub-apps/ec-component-library';
import { Divider, useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { GuestSelect } from '../../../components/NumberSelect';
import { Spacer } from '../../../components/Spacer';
import { Typography } from '../../../components/Typography';
import { addMessageAction } from '../../../data/actions/messagesAction';
import { accommodationPropTypes, addonPropTypes } from '../../../data/models/Experience';
import { trackEvent } from '../../../utils/analytics';
import { formatCurrency, pluralise } from '../../../utils/helpers';
import AddonDetailsModal from './AddonDetailsModal';
import useStyles from './AddonOptionStyles';

const AddonOption = ({
  addon,
  maxGuests,
  onChange,
  value,
  isAccommodation,
  showPrice,
  hasSelectableRoms,
  addMessage,
  accommodation,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const [showModal, setShowModal] = useState(false);

  // More crap accommodation overrides
  const getAddonPrice = () => {
    if (isAccommodation) {
      if (value <= 1) {
        return null;
      }

      // return formatCurrency(addon.price * value) + addon?.unit ? ` per ${addon?.unit}` : '';
      return formatCurrency(addon.price * value);
    }

    return formatCurrency(addon.price);
  };

  useEffect(() => {
    if (isAccommodation && !hasSelectableRoms && (accommodation.success || accommodation.error)) {
      addMessage(
        `BOOKING_ERROR_${new Date().getTime()}`,
        ` Unfortunately, there are no longer ${pluralise(
          addon?.unit ? addon?.unit?.toLowerCase() : 'suites',
          value,
        )} available.
          Please try another day.`,
        'error',
      );
    }
  }, [isAccommodation, hasSelectableRoms]);

  return (
    <Spacer className={classes.addonOption}>
      <Spacer
        direction='vertical'
        style={{ justifyContent: 'space-between' }}
        gap={28}
        className={classes.content}
      >
        <Spacer direction='vertical' gap={16} className={classes.contentText}>
          <Spacer className={classes.header}>
            {/* <Typography>{addon.name}</Typography> */}
            <Typography variant='h3'>{addon.name}</Typography>
          </Spacer>

          <Spacer className={classes.header} direction='vertical'>
            <Typography className={classes.description}>{addon.description}</Typography>

            {addon?.extraContent?.showInfoButton && (
              <Button
                type='text'
                onClick={() => {
                  trackEvent('Addons', 'Click Learn more');
                  setShowModal(true);
                }}
                style={{ button: { fontWeight: theme.fontWeights.regular } }}
              >
                <Typography className={classes.learnMore}>
                  {addon?.extraContent?.infoButtonLabel || 'Learn more'}
                </Typography>
              </Button>
            )}
          </Spacer>
        </Spacer>

        <Spacer direction='vertical' gap={12} style={{ alignItems: 'flex-start', width: '100%' }}>
          {addon.min > 0 && (
            <Spacer gap={12} className={classes.footer}>
              <>
                <Typography>Min {addon.min} people</Typography>
                <Divider variant='vertical' />
              </>
            </Spacer>
          )}

          <Spacer className={classes.header}>
            <GuestSelect
              variant='big'
              onDecrease={() => onChange(value - 1)}
              onIncrease={() => onChange(value + 1)}
              min={isAccommodation && hasSelectableRoms ? 1 : 0}
              max={maxGuests}
              value={value}
              onSetValue={(newValue) => onChange(newValue)}
              unit={addon?.unit ?? ''}
            />
            {showPrice && <Typography className={classes.price}>{getAddonPrice()}</Typography>}
          </Spacer>
        </Spacer>
      </Spacer>

      {addon?.imageUrl && (
        <Box className={classes.imageContainer}>
          <img className={classes.image} src={addon?.imageUrl} alt={`Addon - ${addon?.name}`} />
        </Box>
      )}

      <AddonDetailsModal
        addon={addon}
        onClose={() => setShowModal(false)}
        open={showModal}
        isAccommodation={isAccommodation}
      />
    </Spacer>
  );
};

AddonOption.defaultProps = {
  value: 0,
  unit: '',
  maxGuests: 2,
};

AddonOption.propTypes = {
  addon: addonPropTypes.isRequired,
  maxGuests: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.number,
  unit: PropTypes.string,
  isAccommodation: PropTypes.bool.isRequired,
  showPrice: PropTypes.bool.isRequired,
  hasSelectableRoms: PropTypes.bool.isRequired,
  addMessage: PropTypes.func.isRequired,
  accommodation: accommodationPropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  accommodation: state.accommodation,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      addMessage: addMessageAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(AddonOption);
